<script setup lang="ts">
import type { PublicOrganization } from "~/composables/useRechercheData";
import {
  departmentBySlug,
  departmentInLabel,
  regionBySlug,
  regionInLabel,
} from "#shared/utils/frenchGeo";
import {
  GEO_HUB_MIN_DEPARTMENT,
  isOrgIndexable,
} from "#shared/utils/geoHubThresholds";

const { t } = useI18n({ useScope: "local" });

/**
 * Étage DÉPARTEMENT de l'annuaire — /associations/departement/herault
 *
 * Deux garde-fous, tous deux nécessaires :
 *   1. seuil de substance : au moins GEO_HUB_MIN_DEPARTMENT associations
 *      dignes d'index dans le périmètre ;
 *   2. NON-REDONDANCE : si toutes les associations du département tiennent
 *      dans une seule ville, cette page affiche exactement la même liste que
 *      la page ville. Deux URLs, un contenu → on garde la plus précise (celle
 *      qui correspond à la requête réelle « associations à Lodève ») et on
 *      passe le parent en noindex.
 *
 * La règle 2 s'éteint d'elle-même avec la croissance : à 10 000 associations,
 * plus aucun département ne tiendra dans une seule ville.
 */
const route = useRoute();
const depSlug = (route.params as Record<string, string>).dep ?? "";

const dept = departmentBySlug(depSlug);
if (!dept) {
  throw createError({ statusCode: 404, statusMessage: t('errors.departementIntrouvable') });
}
// Outre-mer : région et département HOMONYMES (Martinique, Saint-Barthélemy…).
// La région y répète la page département — même règle de non-redondance que
// les seuils : pas de lien ni d'étage de fil d'Ariane vers elle.
const regionParente = regionBySlug(dept.regionSlug);
const region = regionParente && regionParente.slug !== dept.slug ? regionParente : null;

const { data, pending } = await useAsyncData(`orgs-dep-${depSlug}`, async () => {
  try {
    return await $fetch<{
      name: string;
      indexableCount: number;
      organizations: Array<PublicOrganization & { city: string | null }>;
      cities: Array<{ slug: string; name: string; count: number }>;
    }>(`/api/organizations/by-geo?level=department&slug=${depSlug}`);
  } catch {
    return { name: dept.name, indexableCount: 0, organizations: [], cities: [] };
  }
});

const organizations = computed(() => data.value?.organizations ?? []);
const cities = computed(() => data.value?.cities ?? []);
const indexableCount = computed(
  () => organizations.value.filter((o) => isOrgIndexable(o)).length,
);

const inLabel = departmentInLabel(dept);
const canonicalUrl = `https://donoor.org/associations/departement/${dept.slug}`;

const seoTitle = computed(
  () => t('seo.titre', { inLabel, code: dept.code }),
);
const seoDescription = computed(
  () =>
    t('seo.description', {
      n: indexableCount.value,
      s: indexableCount.value > 1 ? "s" : "",
      inLabel,
    }),
);

// Redondance : une seule ville peuplée → cette page duplique la page ville.
const isRedundantWithSingleCity = computed(() => cities.value.length <= 1);
const shouldNoindex = computed(
  () =>
    !pending.value &&
    (indexableCount.value < GEO_HUB_MIN_DEPARTMENT ||
      isRedundantWithSingleCity.value),
);
const robotsContent = useRobotsContent(shouldNoindex);

useSeoMeta({
  title: () => seoTitle.value,
  description: () => seoDescription.value,
  ogTitle: () => seoTitle.value,
  ogDescription: () => seoDescription.value,
  ogImage: "https://donoor.org/og-image.png",
  ogUrl: canonicalUrl,
  ogLocale: "fr_FR",
  twitterCard: "summary",
  robots: () => robotsContent.value,
});

useHead({
  link: [{ rel: "canonical", key: "canonical", href: canonicalUrl }],
  meta: computed(() => [{ name: "robots", content: robotsContent.value }]),
  script: computed(() => [
    {
      type: "application/ld+json",
      innerHTML: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: seoTitle.value,
        description: seoDescription.value,
        url: canonicalUrl,
        breadcrumb: {
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Donoor", item: "https://donoor.org" },
            { "@type": "ListItem", position: 2, name: t('breadcrumb.associations'), item: "https://donoor.org/recherche/associations" },
            ...(region
              ? [{
                  "@type": "ListItem",
                  position: 3,
                  name: region.name,
                  item: `https://donoor.org/associations/region/${region.slug}`,
                }]
              : []),
            { "@type": "ListItem", position: region ? 4 : 3, name: dept.name, item: canonicalUrl },
          ],
        },
        // ItemList (SEO 2026-08-29) : sans lui, la page est « une page avec des
        // liens » pour les moteurs et les LLM — avec, c'est une LISTE de N
        // associations, ce que les hubs ville et catégorie déclarent déjà.
        ...(organizations.value.length > 0 && {
          mainEntity: {
            "@type": "ItemList",
            numberOfItems: organizations.value.length,
            itemListElement: organizations.value.slice(0, 50).map((o, i) => ({
              "@type": "ListItem",
              position: i + 1,
              item: { "@type": "NGO", name: o.name, url: `https://donoor.org/associations/${o.slug}` },
            })),
          },
        }),
      }),
    },
  ]),
});
</script>

<template>
  <div class="min-h-screen bg-white">
    <RechercheUnifiedHero
      active-type="associations"
      :h1="t('hero.h1', { inLabel })"
      :intro="t('hero.intro', { n: indexableCount, s: indexableCount > 1 ? 's' : '', inLabel, code: dept.code })"
    />

    <div class="px-4 py-10 sm:py-12">
      <UContainer>
        <!-- Maillage vers les villes : donne à Google un chemin vers les fiches,
             au lieu de dépendre du seul sitemap. -->
        <nav v-if="cities.length" class="mb-10" :aria-label="t('villes.ariaLabel')">
          <h2 class="text-lg font-semibold text-gray-900 mb-3">
            {{ t('villes.titre', { inLabel }) }}
          </h2>
          <ul class="flex flex-wrap gap-2">
            <li v-for="c in cities" :key="c.slug">
              <NuxtLink
                :to="`/associations/ville/${c.slug}`"
                class="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-gray-200 text-sm text-gray-700 hover:bg-gray-50"
              >
                {{ c.name }}
                <span class="text-xs text-gray-400">{{ c.count }}</span>
              </NuxtLink>
            </li>
          </ul>
        </nav>

        <div v-if="pending" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          <div v-for="i in 6" :key="i" class="h-56 rounded-2xl bg-gray-100 animate-pulse" />
        </div>

        <div v-else-if="organizations.length" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          <RechercheOrganizationCard v-for="(org, i) in organizations" :key="org.id" :org="org" :priority="i < 3" />
        </div>

        <div v-else class="text-center py-16">
          <UIcon name="i-lucide-building-2" class="size-12 text-gray-300 mx-auto mb-4" />
          <p class="text-gray-500 mb-4">
            {{ t('vide.message', { inLabel }) }}
          </p>
          <NuxtLink to="/recherche/associations" class="text-sm text-(--color-main) hover:underline">
            {{ t('vide.voirToutes') }}
          </NuxtLink>
        </div>

        <i18n-t
          v-if="region"
          keypath="voirAussi.texte"
          tag="p"
          class="mt-10 text-sm text-gray-500"
          scope="parent"
        >
          <template #lien>
            <NuxtLink
              :to="`/associations/region/${region.slug}`"
              class="text-(--color-main) hover:underline"
            >
              {{ t('voirAussi.lien', { region: regionInLabel(region) }) }}
            </NuxtLink>
          </template>
        </i18n-t>
      </UContainer>
    </div>
  </div>
</template>

<i18n lang="json" src="../../../../i18n/pages/pages/associations/departement/[dep].json"></i18n>
